import { useTranslation } from 'react-i18next';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  Chart as ChartJS,
  BarController,
  LineController,
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import NoData from './NoData';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
dayjs.extend(isBetween);
const ONE_DAY = 24 * 60 * 60 * 1000;
const SEVEN_DAYS = 7 * ONE_DAY;
const THIRTY_DAYS = 30 * ONE_DAY;

ChartJS.register(
  BarController,
  LineController,
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  zoomPlugin
);

const options = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      min: 0,
      beginAtZero: true,
      suggestedMax: 10,
      border: {
        display: false,
      },
      ticks: {
        color: 'white',
        beginAtZero: true,
        callback: function (value) {
          if (value % 1 === 0) {
            return value;
          }
        },
      },
      grid: { display: true, color: 'rgba(182, 186, 182, 0.3)' },
    },
    x: {
      type: 'time',
      stacked: false,
      // offset: false,
      border: {
        display: false,
      },
      ticks: {
        color: 'white',
        maxTicksLimit: 15,
      },
      grid: {
        display: false,
      },
      time: {
        unit: 'day',
        displayFormats: {
          day: 'DD/MM',
        },
        tooltipFormat: 'DD/MM/YYYY',
      },
    },
  },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        color: 'white',
        usePointStyle: true,
        generateLabels: (chart) => {
          const labels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);

          labels.forEach((label) => {
            const ds = chart.data.datasets[label.datasetIndex];

            if (ds.type === 'line') {
              label.pointStyle = 'line';
            }

            if (ds.type === 'bar') {
              label.pointStyle = 'rect';
            }
          });

          return labels;
        },
      },
    },
    zoom: {
      pan: {
        enabled: true,
        mode: 'x',
      },
      zoom: {
        wheel: {
          enabled: false,
        },
        pinch: {
          enabled: true,
        },
        mode: 'x',
      },
      limits: {
        x: {
          min: 'original',
          max: 'original',
          minRange: SEVEN_DAYS,
        },
        y: {
          min: 0,
        },
      },
    },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          if (ctx.dataset.datasetKey === 'salesOnline') {
            const realValue = ctx.dataset.realData[ctx.dataIndex].y;
            return `${realValue} (RON) Online`;
          }
          if (ctx.dataset.datasetKey === 'salesOffline') {
            const realValue = ctx.dataset.realData[ctx.dataIndex].y;
            return `${realValue} (RON) Offline`;
          }
        },
      },
    },
  },
};

export default function SalesChart({ saleStatistics }) {
  const { t } = useTranslation();

  const chartRef = useRef(null);

  const { onlineDates, offlineDates, onlinePriceDates, offlinePriceDates, dates } = saleStatistics;
  const user = useSelector((state) => state.auth.user);
  const today = dayjs().endOf('day');

  const isData = useMemo(() => {
    if (user.role === 'organizer') {
      return (
        (Array.isArray(onlineDates) && onlineDates.length > 0) ||
        (Array.isArray(offlineDates) && offlineDates.length > 0)
      );
    } else {
      return Array.isArray(dates) && dates.length > 0;
    }
  }, [user.role, onlineDates, offlineDates, dates]);

  const {
    cleanOnlineDates,
    cleanOfflineDates,
    normalizedOfflinePriceDates,
    cleanOfflinePriceDates,
    normalizedOnlinePriceDates,
    cleanOnlinePriceDates,
    cleanDates,
    minDate,
    maxDate,
  } = useMemo(() => {
    if (!isData) {
      return {
        cleanOnlineDates: [],
        cleanOfflineDates: [],
        normalizedOfflinePriceDates: [],
        cleanOfflinePriceDates: [],
        normalizedOnlinePriceDates: [],
        cleanOnlinePriceDates: [],
        cleanDates: [],
        minDate: null,
        maxDate: null,
      };
    }

    const today = dayjs().endOf('day');

    const cleanDatesByToday = (arr) =>
      (arr ?? []).filter((day) => dayjs(day.x).isBefore(today) || dayjs(day.x).isSame(today));

    const cleanOnlineDates = cleanDatesByToday(onlineDates);
    const cleanOfflineDates = cleanDatesByToday(offlineDates);
    const cleanOfflinePriceDates = cleanDatesByToday(offlinePriceDates);
    const cleanOnlinePriceDates = cleanDatesByToday(onlinePriceDates);
    const cleanDates = cleanDatesByToday(dates);

    const maxTicketsOffline =
      user.role === 'organizer'
        ? Math.max(...cleanOfflineDates.map((d) => d.y))
        : Math.max(...cleanDates.map((d) => d.y));
    const salesValuesOffline = cleanOfflinePriceDates.map((d) => d.y);
    const maxSalesOffline = Math.max(...salesValuesOffline);
    const normalizedOfflinePriceDates = cleanOfflinePriceDates.map((d) => ({
      x: d.x,
      y: maxSalesOffline === 0 ? 0 : (d.y / maxSalesOffline) * maxTicketsOffline,
    }));

    const maxTicketsOnline = Math.max(...cleanOnlineDates.map((d) => d.y));
    const salesValuesOnline = cleanOnlinePriceDates.map((d) => d.y);
    const maxSalesOnline = Math.max(...salesValuesOnline);
    const normalizedOnlinePriceDates = cleanOnlinePriceDates.map((d) => ({
      x: d.x,
      y: (d.y / maxSalesOnline) * maxTicketsOnline,
    }));

    let minDate = null;
    let maxDate = null;

    if (onlineDates.length > 0) {
      if (onlineDates.length === 1) {
        maxDate = dayjs(today) + ONE_DAY;
      } else {
        maxDate = Math.max(...onlineDates.map((d) => dayjs(d.x).valueOf()));
        minDate = Math.min(...onlineDates.map((d) => dayjs(d.x).valueOf()));
      }
    }

    return {
      cleanOnlineDates,
      cleanOfflineDates,
      normalizedOfflinePriceDates,
      cleanOfflinePriceDates,
      normalizedOnlinePriceDates,
      cleanOnlinePriceDates,
      cleanDates,
      minDate,
      maxDate,
    };
  }, [isData, onlineDates, offlineDates, offlinePriceDates, onlinePriceDates, dates, user.role]);

  const dynamicOptions = useMemo(
    () => ({
      ...options,
      scales: {
        ...options.scales,
        x: {
          ...options.scales.x,
          min: minDate ?? undefined,
          max: maxDate ?? undefined,
        },
      },
    }),
    [minDate, maxDate]
  );

  const chartData = useMemo(() => {
    if (user.role === 'organizer') {
      return {
        datasets: [
          {
            type: 'line',
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'transparent',
            pointBorderColor: 'rgb(122, 188, 235)',
            pointBorderWidth: 2,
            label: t('salesCurrencyOffline'),
            datasetKey: 'salesOffline',
            data: normalizedOfflinePriceDates,
            realData: cleanOfflinePriceDates,
            tension: 0.3,
            borderColor: 'rgb(122, 188, 235, 0.7)',
          },
          {
            type: 'line',
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'transparent',
            pointBorderColor: 'rgb(166, 202, 168)',
            pointBorderWidth: 2,
            label: t('salesCurrencyOnline'),
            datasetKey: 'salesOnline',
            data: normalizedOnlinePriceDates,
            realData: cleanOnlinePriceDates,
            tension: 0.3,
            borderColor: 'rgb(166, 202, 168, 0.7)',
          },
          {
            type: 'bar',
            pointStyle: 'rect',
            label: t('Online Sales'),
            data: cleanOnlineDates,
            barThickness: 15,
            borderColor: 'rgba(102, 187, 106, 1)',
            backgroundColor: 'rgba(102, 187, 106)',
          },
          {
            type: 'bar',
            pointStyle: 'rect',
            label: t('Offline Sales'),
            data: cleanOfflineDates,
            barThickness: 15,
            borderColor: 'rgba(10, 141, 219, 1)',
            backgroundColor: 'rgba(10, 141, 219)',
          },
        ],
      };
    } else {
      return {
        datasets: [
          {
            type: 'bar',
            pointStyle: 'rect',
            label: t('Sales'),
            data: cleanDates,
            barThickness: 15,
            borderColor: 'rgba(102, 187, 106, 1)',
            backgroundColor: 'rgba(102, 187, 106)',
          },
          {
            type: 'line',
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'transparent',
            pointBorderColor: 'rgb(166, 202, 168)',
            pointBorderWidth: 2,
            label: t('salesCurrency'),
            datasetKey: 'salesOffline',
            data: normalizedOfflinePriceDates,
            realData: cleanOfflinePriceDates,
            tension: 0.3,
            borderColor: 'rgba(102, 187, 106, 0.7)',
          },
        ],
      };
    }
  }, [
    user.role,
    t,
    cleanOnlineDates,
    normalizedOnlinePriceDates,
    cleanOnlinePriceDates,
    cleanOfflineDates,
    normalizedOfflinePriceDates,
    cleanOfflinePriceDates,
    cleanDates,
  ]);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = chartRef.current;
    const scale = chart.scales.x;
    if (!scale) return;

    const fullMin = scale.min;
    const fullMax = scale.max < today ? scale.max : today;

    const range = fullMax - fullMin;

    let targetMin;
    let targetMax = fullMax;

    if (range > THIRTY_DAYS) {
      targetMin = fullMax - THIRTY_DAYS;
    } else {
      targetMin = fullMin;
    }

    chart.zoomScale('x', {
      min: targetMin,
      max: targetMax,
    });
  }, [chartData, dynamicOptions, today]);

  const panDays = (days) => {
    const chart = chartRef.current;
    if (!chart) return;

    const scale = chart.scales.x;
    if (!scale) return;

    const visibleRange = scale.max - scale.min;
    const msPerPixel = visibleRange / scale.width;

    const msToMove = days * ONE_DAY;
    const pixelsToMove = msToMove / msPerPixel;

    chart.pan({ x: pixelsToMove });
  };

  const scrollLeft = () => panDays(1);

  const scrollRight = () => panDays(-1);

  const zoomIn = () => {
    const chart = chartRef.current;
    if (!chart) return;

    chart.zoom(1.2);
  };

  const zoomOut = () => {
    const chart = chartRef.current;
    if (!chart) return;

    chart.zoom(0.8);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="h5">{t('Sales chart')}</Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={scrollLeft}>
            <ChevronLeftIcon />
          </IconButton>

          <IconButton onClick={scrollRight}>
            <ChevronRightIcon />
          </IconButton>

          <IconButton onClick={zoomIn}>
            <ZoomInIcon />
          </IconButton>

          <IconButton onClick={zoomOut}>
            <ZoomOutIcon />
          </IconButton>
        </Box>
      </Box>
      {isData ? (
        <Paper sx={{ position: 'relative', height: 400, width: '100%', p: 2, mb: 2, mt: 2 }}>
          <Line ref={chartRef} options={dynamicOptions} data={chartData} />
        </Paper>
      ) : (
        <NoData />
      )}
    </>
  );
}
